import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    createFilterOptions,
    FormControlLabel,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { TESTO_OGNI_GRUPPO, chiaveConcessione } from './concessioni';

// La ricerca sui gruppi guarda anche il code e le code che il gruppo contiene: chi cerca
// «a chi do la coda Fatture passive» scrive il nome della coda, non quello del gruppo.
const filtraGruppi = createFilterOptions({
    stringify: (g) => [g.label, g.code, ...(g.candidateGroups || [])].join(' '),
});
const filtraRuoli = createFilterOptions({
    stringify: (r) => [r.label, r.code, r.description].filter(Boolean).join(' '),
});

const contaCode = (gruppo) => {
    const n = (gruppo.candidateGroups || []).length;
    if (n === 0) {
        return 'nessuna coda';
    }
    return n === 1 ? '1 coda' : `${n} code`;
};

/**
 * «Concedi un ruolo»: prepara una o piu' concessioni, senza scriverle.
 *
 * UN ruolo e UNO O PIU' gruppi. Ruoli e gruppi non hanno un numero massimo, percio' si
 * cercano invece di scorrerli; e il caso comune — lo stesso ruolo su piu' gruppi — si
 * prepara in un passaggio solo invece di uno per gruppo. «Ogni gruppo» non sta nell'elenco
 * dei gruppi ma e' una scelta a parte: e' la piu' larga che esista, e accanto alle voci
 * ordinarie si sceglieva per sbaglio. Il default e' la scelta stretta.
 *
 * Le concessioni preparate risalgono al pannello con `onPrepara`, che le accumula fino al
 * salvataggio; `onErrore` riceve il motivo per cui non se n'e' preparata nessuna, o `null`.
 */
export default function FormConcessione({ catalog, groups, chiaviAttive, onPrepara, onErrore }) {
    const [nuovoRuolo, setNuovoRuolo] = useState(null);
    const [ambitoNuovo, setAmbitoNuovo] = useState('alcuni');
    const [nuoviGruppi, setNuoviGruppi] = useState([]);

    const nuoveDaPreparare = !nuovoRuolo
        ? 0
        : ambitoNuovo === 'tutti' ? 1 : nuoviGruppi.length;

    const giaConcesso = (g) => Boolean(nuovoRuolo)
        && chiaviAttive.has(chiaveConcessione(nuovoRuolo.id, g.id));

    const handleGrant = () => {
        if (!nuovoRuolo || nuoveDaPreparare === 0) {
            return;
        }
        onErrore(null);
        const destinazioni = ambitoNuovo === 'tutti'
            ? [{ id: null, label: null }]
            : nuoviGruppi;
        // Le concessioni gia' presenti o preparate si spengono nell'elenco dei gruppi, e qui
        // si scartano: detto prima e non dal 409 del backend al salvataggio, che arriverebbe
        // dopo aver preparato altre modifiche senza dire quale lo ha provocato.
        const nuove = destinazioni.filter(
            (g) => !chiaviAttive.has(chiaveConcessione(nuovoRuolo.id, g.id)));
        if (nuove.length === 0) {
            onErrore(ambitoNuovo === 'tutti'
                ? 'Questo ruolo è già concesso su ogni gruppo.'
                : 'Questo ruolo è già concesso in quei gruppi.');
            return;
        }
        onPrepara(nuove.map((g) => ({
            tipo: 'aggiunta',
            roleId: nuovoRuolo.id,
            roleLabel: nuovoRuolo.label || nuovoRuolo.id,
            groupId: g.id,
            groupLabel: g.label,
        })));
        setNuovoRuolo(null);
        setNuoviGruppi([]);
        setAmbitoNuovo('alcuni');
    };

    return (
        <Box
            sx={{
                mt: 1,
                p: 1.5,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
            }}
        >
            <Typography variant="subtitle2" gutterBottom>
                Concedi un ruolo
            </Typography>
            <Autocomplete
                size="small"
                options={catalog}
                value={nuovoRuolo}
                onChange={(_, ruolo) => setNuovoRuolo(ruolo)}
                filterOptions={filtraRuoli}
                getOptionLabel={(r) => r.label || r.code || r.id}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                noOptionsText="Nessun ruolo trovato"
                renderOption={({ key, ...props }, r) => (
                    <li key={key} {...props}>
                        <Box>
                            <Typography variant="body2">{r.label}</Typography>
                            {(r.permissionCount != null || r.description) && (
                                <Typography variant="caption" color="text.secondary">
                                    {[
                                        r.permissionCount != null
                                            ? `${r.permissionCount} permessi`
                                            : null,
                                        r.description || null,
                                    ].filter(Boolean).join(' · ')}
                                </Typography>
                            )}
                        </Box>
                    </li>
                )}
                renderInput={(params) => (
                    <TextField {...params} label="Ruolo" placeholder="Cerca un ruolo" />
                )}
            />

            <RadioGroup
                row
                value={ambitoNuovo}
                onChange={(e) => setAmbitoNuovo(e.target.value)}
                sx={{ mt: 1 }}
            >
                <FormControlLabel
                    value="alcuni"
                    control={<Radio size="small" />}
                    label={<Typography variant="body2">In alcuni gruppi</Typography>}
                />
                <FormControlLabel
                    value="tutti"
                    control={<Radio size="small" />}
                    label={<Typography variant="body2">{TESTO_OGNI_GRUPPO}</Typography>}
                />
            </RadioGroup>

            {ambitoNuovo === 'tutti' && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                    Il ruolo varrà su tutti i gruppi, anche quelli creati
                    in futuro. È la concessione più ampia.
                </Alert>
            )}

            {ambitoNuovo === 'alcuni' && (
                <Autocomplete
                    multiple
                    size="small"
                    disableCloseOnSelect
                    limitTags={4}
                    options={groups}
                    value={nuoviGruppi}
                    onChange={(_, scelti) => setNuoviGruppi(scelti)}
                    filterOptions={filtraGruppi}
                    getOptionLabel={(g) => g.label || g.code || g.id}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    getOptionDisabled={giaConcesso}
                    noOptionsText={groups.length === 0
                        ? 'Nessun gruppo disponibile'
                        : 'Nessun gruppo trovato'}
                    renderOption={({ key, ...props }, g, { selected }) => (
                        <li key={key} {...props}>
                            <Checkbox
                                size="small"
                                checked={selected || giaConcesso(g)}
                                sx={{ mr: 1, p: 0.5 }}
                            />
                            <Box>
                                <Typography variant="body2">{g.label}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {giaConcesso(g)
                                        ? 'già concesso'
                                        : [
                                            contaCode(g),
                                            (g.candidateGroups || []).slice(0, 3).join(', '),
                                        ].filter(Boolean).join(' · ')}
                                </Typography>
                            </Box>
                        </li>
                    )}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Gruppi"
                            placeholder={nuoviGruppi.length === 0
                                ? 'Cerca per gruppo o per coda'
                                : ''}
                        />
                    )}
                    sx={{ mt: 1 }}
                />
            )}

            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
                <Button
                    variant="outlined"
                    onClick={handleGrant}
                    disabled={nuoveDaPreparare === 0}
                >
                    {nuoveDaPreparare > 1
                        ? `Aggiungi ${nuoveDaPreparare} concessioni`
                        : 'Aggiungi'}
                </Button>
            </Stack>
        </Box>
    );
}

FormConcessione.propTypes = {
    catalog: PropTypes.array.isRequired,
    groups: PropTypes.array.isRequired,
    chiaviAttive: PropTypes.instanceOf(Set).isRequired,
    onPrepara: PropTypes.func.isRequired,
    onErrore: PropTypes.func.isRequired,
};
